import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Popconfirm, message, Space, Typography, Select, Spin } from 'antd';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import useUserProfile from '../../useUserProfile';
import axios from 'axios';
import * as XLSX from 'xlsx';

import { use } from 'i18next';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const API_URL = process.env.REACT_APP_API_URL;

const ManagerBudgetAdminPanel: React.FC = () => {
    const [budgets, setBudgets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingBudget, setEditingBudget] = useState<any>(null);
    const [form] = Form.useForm();
    const [searchTerm, setSearchTerm] = useState('');
    const [managerOptions, setManagerOptions] = useState<any[]>([]);
    const [selectedManager, setSelectedManager] = useState<any>(null);
    const [searching, setSearching] = useState(false);
    const [UserDetails, setUserDetails] = useState<{ id: string, displayName: string, email: string }[]>([]);
    const { requestProfileData } = useUserProfile();
    const [tableSearch, setTableSearch] = useState('');

    const { instance, accounts } = useMsal();

    // Filtered budgets based on search
    const filteredBudgets = budgets
        .filter(b =>
            !tableSearch ||
            (b.managerName && b.managerName.toLowerCase().includes(tableSearch.toLowerCase())) ||
            (b.managerEmail && b.managerEmail.toLowerCase().includes(tableSearch.toLowerCase()))
        )
        .sort((a, b) => {
            const nameA = (a.managerName || '').toLowerCase();
            const nameB = (b.managerName || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

    // Export to Excel
    const exportToExcel = () => {
        const dataToExport = filteredBudgets.map(budget => ({
            'Manager Name': budget.managerName || '-',
            'Manager Email': budget.managerEmail || '-',
            'Country': budget.country || '-',
            'Total Budget': budget.totalBudget,
            'Used Budget': budget.usedBudget,
            'Balance': budget.balance,
            'Reportees Count': budget.reporteesCount,
            'Comments': budget.comments || '-'
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Manager Budgets');
        
        // Generate file name with current date
        const fileName = `Manager_Budgets_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        message.success('Excel file exported successfully');
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                await requestProfileData();
                const userMail = localStorage.getItem("UserMail");
                const userName = localStorage.getItem("UserName");
                const userId = localStorage.getItem("UserId");
                setUserDetails([{ id: userId || '', displayName: userName || 'Default Nominator', email: userMail || '' }]);

            } catch (error) {
                console.error('Error fetching profile data:', error);
            }
        };

        fetchProfileData();
    }, [requestProfileData]);
    const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });
        return res.idToken;
    };

    // Fetch all manager budgets
    const fetchBudgets = async () => {
        setLoading(true);
        try {
            const token = await fetchToken();
            const res = await axios.get(`${API_URL}/api/budget/manager`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBudgets(res.data);
        } catch (err) {
            message.error('Failed to fetch manager budgets');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchBudgets();
    }, []);

    // Search managers by name or GUID
    const fetchManagers = async (term: string) => {
        if (!term || term.length < 3) {
            setManagerOptions([]);
            return;
        }
        setSearching(true);
        try {
            const token = await fetchToken();
            const res = await axios.get(`${API_URL}/api/Users/search?search=${encodeURIComponent(term)}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setManagerOptions(res.data);
        } catch {
            setManagerOptions([]);
        }
        setSearching(false);
    };

    // Open modal for create/edit
    const openModal = (budget?: any) => {
        setEditingBudget(budget || null);
        setModalVisible(true);
        if (budget) {
            setSelectedManager({
                id: budget.managerId,
                displayName: budget.managerName || budget.managerId
            });
            form.setFieldsValue({
                ManagerId: budget.managerId,
                TotalBudget: budget.totalBudget,
                UsedBudget: budget.usedBudget,
                Balance: budget.balance,
                Comments: budget.comments,
                ReporteesCount: budget.reporteesCount,
            });
        } else {
            setSelectedManager(null);
            form.resetFields();
        }
    };

    // Handle create or update
    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const token = await fetchToken();
            const userId = UserDetails[0].id;
            if (!userId) {
                message.error('User ID not found');
                return;
            }
            if (editingBudget) {
                // Update
                await axios.put(`${API_URL}/api/budget/manager`, {
                    ManagerBudgetId: editingBudget.managerBudgetId,
                    TotalBudget: values.TotalBudget,
                    UsedBudget: values.UsedBudget,
                    Balance: values.Balance,
                    Comments: values.Comments,
                    ReporteesCount: values.ReporteesCount,
                    UpdatedBy: userId,
                }, { headers: { Authorization: `Bearer ${token}` } });
                message.success('Manager budget updated');
            } else {
                // Create
                await axios.post(`${API_URL}/api/budget/manager`, {
                    ManagerId: values.ManagerId,
                    TotalBudget: values.TotalBudget,
                    AdminId: UserDetails[0].id,
                    Comments: values.Comments,
                    ReporteesCount: values.ReporteesCount,
                }, { headers: { Authorization: `Bearer ${token}` } });
                message.success('Manager budget created');
            }
            setModalVisible(false);
            setEditingBudget(null);
            form.resetFields();
            fetchBudgets();
        } catch (err: any) {
            // Try to extract a string error message
            const errorMsg =
                err?.response?.data?.title ||
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                (typeof err?.response?.data === 'string' ? err.response.data : null) ||
                'Operation failed';
            message.error(errorMsg);
        }
    };

    // Handle delete
    const handleDelete = async (id: string) => {
        try {
            const token = await fetchToken();
            await axios.delete(`${API_URL}/api/budget/manager/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success('Manager budget deleted');
            fetchBudgets();
        } catch {
            message.error('Failed to delete');
        }
    };

    const columns = [
        {
            title: 'Manager',
            dataIndex: 'managerName',
            key: 'managerName',
            render: (_: any, record: any) => (
                <div>
                    <div><b>{record.managerName || '-'}</b></div>
                    <div style={{ color: '#888', fontSize: 12 }}>{record.managerEmail || '-'}</div>
                </div>
            ),
            width: 200,
            fixed: 'left' as const
        },
        { title: 'Country', dataIndex: 'country', key: 'country', width: 120 },
        { title: 'Total Budget', dataIndex: 'totalBudget', key: 'totalBudget', width: 120 },
        { title: 'Used Budget', dataIndex: 'usedBudget', key: 'usedBudget', width: 120 },
        { title: 'Balance', dataIndex: 'balance', key: 'balance', width: 100 },
        { title: 'Reportees', dataIndex: 'reporteesCount', key: 'reporteesCount', width: 100 },
        { title: 'Comments', dataIndex: 'comments', key: 'comments', width: 250 },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            fixed: 'right' as const,
            render: (_: any, record: any) => (
                <Space>
                    <Button type="link" onClick={() => openModal(record)}>Edit</Button>
                    <Popconfirm title="Delete this manager budget?" onConfirm={() => handleDelete(record.managerBudgetId)}>
                        <Button type="link" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),

        },
    ];

    return (
        <div >
            <Title level={3}>Manager Budget Management</Title>
            <Space style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={() => openModal()}>
                    Create Manager Budget
                </Button>
                <Button 
                    type="default" 
                    icon={<DownloadOutlined />} 
                    onClick={exportToExcel}
                    disabled={filteredBudgets.length === 0}
                >
                    Export to Excel
                </Button>
            </Space>
            <Input
                placeholder="Search by manager name or email"
                prefix={<SearchOutlined />}
                value={tableSearch}
                onChange={e => setTableSearch(e.target.value)}
                style={{ width: 300, marginBottom: 16, marginLeft: 16 }}
                allowClear
            />
            <Table
                columns={columns}
                dataSource={filteredBudgets}
                rowKey="managerBudgetId"
                loading={loading}
                bordered
                scroll={{ x: 1160 }}
            />

            {/* <Modal
                title={editingBudget ? 'Edit Manager Budget' : 'Create Manager Budget'}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => { setModalVisible(false); setEditingBudget(null); }}
                okText={editingBudget ? 'Update' : 'Create'}
            >
                <Form form={form} layout="vertical">
                    {!editingBudget && (
                        <>
                            <Form.Item
                                label="Manager"
                                name="ManagerId"
                                rules={[{ required: true, message: 'Manager is required' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Search manager by name or email"
                                    filterOption={false}
                                    onSearch={fetchManagers}
                                    onChange={(_, option: any) => setSelectedManager(option)}
                                    notFoundContent={searching ? <Spin size="small" /> : null}
                                >
                                    {managerOptions.map(user => (
                                        <Option key={user.id} value={user.id}>
                                            {user.displayName} ({user.email})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>
                    )}
                    <Form.Item
                        label="Total Budget"
                        name="TotalBudget"
                        rules={[{ required: true, message: 'Total Budget is required' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item label="Comments" name="Comments">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item
                        label="Reportees Count"
                        name="ReporteesCount"
                        rules={[{ required: true, message: 'Reportees count is required' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal> */}
            <Modal
                title={editingBudget ? 'Edit Manager Budget' : 'Create Manager Budget'}
                open={modalVisible}
                onOk={handleOk}
                onCancel={() => { setModalVisible(false); setEditingBudget(null); }}
                okText={editingBudget ? 'Update' : 'Create'}
            >
                <Form form={form} layout="vertical">
                    {!editingBudget ? (
                        <>
                            <Form.Item
                                label="Manager"
                                name="ManagerId"
                                rules={[{ required: true, message: 'Manager is required' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Search manager by name or email"
                                    filterOption={false}
                                    onSearch={fetchManagers}
                                    onChange={(_, option: any) => setSelectedManager(option)}
                                    notFoundContent={searching ? <Spin size="small" /> : null}
                                >
                                    {managerOptions.map(user => (
                                        <Option key={user.id} value={user.id}>
                                            {user.displayName} ({user.email})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </>
                    ) : (
                        <div style={{ marginBottom: 16 }}>
                            <b>Manager:</b> {editingBudget.managerName || '-'}<br />
                            <span style={{ color: '#888', fontSize: 12 }}>{editingBudget.managerEmail || '-'}</span>
                        </div>
                    )}
                    <Form.Item
                        label="Total Budget"
                        name="TotalBudget"
                        rules={[{ required: true, message: 'Total Budget is required' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    {editingBudget && (
                        <>
                            <Form.Item
                                label="Used Budget"
                                name="UsedBudget"
                                initialValue={editingBudget.usedBudget}
                                rules={[{ required: true, message: 'Used Budget is required' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item
                                label="Balance"
                                name="Balance"
                                initialValue={editingBudget.balance}
                                rules={[{ required: true, message: 'Balance is required' }]}
                            >
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </>
                    )}
                    <Form.Item label="Comments" name="Comments">
                        <Input.TextArea />
                    </Form.Item>
                    <Form.Item
                        label="Reportees Count"
                        name="ReporteesCount"
                        rules={[{ required: true, message: 'Reportees count is required' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ManagerBudgetAdminPanel;